import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase, isSupabaseConfigured } from '../../lib/supabase.js';

export default function Portfolio() {
  const [selectedWork, setSelectedWork] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWorks();
  }, []);

  const loadWorks = async () => {
    // Error handling: Check if Supabase is configured before trying to load
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .storage
        .from('works')
        .list('', { limit: 100, sortBy: { column: 'created_at', order: 'desc' } });

      if (error) throw error;

      // Csoportosítjuk munka név szerint
      const groupedWorks = {};
      data.forEach(file => {
        const match = file.name.match(/^(.+?)_\d+_\d+\./);
        if (match) {
          const workName = match[1];
          if (!groupedWorks[workName]) {
            groupedWorks[workName] = [];
          }
          
          const { data: urlData } = supabase.storage
            .from('works')
            .getPublicUrl(file.name);
          
          groupedWorks[workName].push({
            name: file.name,
            url: urlData.publicUrl
          });
        }
      });

      // Csak az első 6 munkát mutatjuk a főoldalon
      const worksArray = Object.entries(groupedWorks)
        .map(([name, files]) => ({
          title: name,
          images: files, // Összes kép, nem csak az első!
          mainImage: files[0]?.url,
          imageCount: files.length
        }))
        .slice(0, 6);

      setWorks(worksArray);
    } catch (error) {
      console.error('Hiba a munkák betöltésekor:', error);
    } finally {
      setLoading(false);
    }
  };

  const openGallery = (work, imageIndex = 0) => {
    setSelectedWork(work);
    setSelectedImageIndex(imageIndex);
    
    // Letiltjuk a scroll-t (nem ugrik fel!)
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
  };

  const closeGallery = () => {
    setSelectedWork(null);
    setSelectedImageIndex(0);
    
    // Visszaállítjuk a scroll-t
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    document.body.style.touchAction = '';
  };

  const nextImage = () => {
    if (selectedWork && selectedImageIndex < selectedWork.images.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }
  };

  const prevImage = () => {
    if (selectedWork && selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedWork) return;

      switch (e.key) {
        case 'Escape':
          closeGallery();
          break;
        case 'ArrowLeft':
          prevImage();
          break;
        case 'ArrowRight':
          nextImage();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWork, selectedImageIndex]);

  // Touch/Swipe navigation (mobile)
  useEffect(() => {
    if (!selectedWork) return;

    let touchStartX = 0;
    let touchEndX = 0;

    const handleTouchStart = (e) => {
      touchStartX = e.changedTouches[0].screenX;
    };

    const handleTouchEnd = (e) => {
      touchEndX = e.changedTouches[0].screenX;
      handleSwipe();
    };

    const handleSwipe = () => {
      const swipeThreshold = 50; // Minimum swipe distance in pixels
      
      if (touchStartX - touchEndX > swipeThreshold) {
        // Swipe left -> next image
        nextImage();
      } else if (touchEndX - touchStartX > swipeThreshold) {
        // Swipe right -> previous image
        prevImage();
      }
    };

    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWork, selectedImageIndex]);

  return (
    <>
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-[var(--container-max)] px-4">
          {/* Section Header */}
          <div className="text-center mb-12 md:mb-16">
            <div className="text-sm font-semibold text-[var(--color-accent)] uppercase tracking-wider mb-3">
              Munkáink
            </div>
            <h2 className="text-3xl md:text-4xl font-[var(--font-serif)] font-semibold text-[var(--color-text-primary)] tracking-tight mb-4">
              Referenciák és elkészült projektek
            </h2>
            <p className="text-lg text-[var(--color-text-secondary)] max-w-2xl mx-auto">
              Nézd meg legújabb egyedi bútor projektjeinket
            </p>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[var(--color-accent)] border-t-transparent"></div>
              <p className="text-gray-600 mt-4">Képek betöltése...</p>
            </div>
          ) : works.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-600 text-lg">Még nincsenek feltöltött munkák.</p>
            </div>
          ) : (
            <>
              {/* Gallery Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {works.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={false}
                    className="group cursor-pointer"
                    onClick={() => openGallery(item, 0)}
                  >
                    <div className="relative overflow-hidden rounded-2xl shadow-[var(--shadow-subtle)] md:hover:shadow-[var(--shadow-md)] transition-shadow duration-200">
                      {/* Image */}
                      <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                        <img
                          src={item.mainImage}
                          alt={item.title}
                          className="w-full h-full object-cover md:group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>

                      {/* Overlay - Always visible on mobile, hover on desktop */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-end p-6">
                        <h3 className="text-white font-semibold text-xl">{item.title}</h3>
                        {item.imageCount > 1 && (
                          <p className="text-white/80 text-sm mt-1">
                            {item.imageCount} kép
                          </p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* View More Button */}
              <div className="text-center mt-12">
                <Link
                  to="/works"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[var(--color-accent)] text-white font-semibold rounded-lg hover:bg-[var(--color-accent-hover)] transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5"
                >
                  További munkák megtekintése
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Lightbox Modal with Gallery */}
      <AnimatePresence>
        {selectedWork && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90"
            onClick={closeGallery}
          >
            {/* Close Button */}
            <button
              onClick={closeGallery}
              className="absolute top-4 right-4 z-10 text-white hover:text-[var(--color-accent)] transition-colors bg-white/10 hover:bg-white/20 rounded-full p-2"
              aria-label="Close lightbox"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Previous Button */}
            {selectedImageIndex > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 text-white rounded-full p-4 transition-colors backdrop-blur-sm"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {/* Next Button */}
            {selectedImageIndex < selectedWork.images.length - 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 text-white rounded-full p-4 transition-colors backdrop-blur-sm"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              className="relative max-w-4xl w-full flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Image */}
              <img
                src={selectedWork.images[selectedImageIndex]?.url}
                alt={selectedWork.title}
                className="max-w-full max-h-[calc(100vh-200px)] object-contain rounded-2xl shadow-2xl"
              />

              {/* Caption */}
              <div className="mt-6 text-center">
                <h3 className="text-white text-2xl font-semibold">{selectedWork.title}</h3>
                <p className="text-white/80 mt-2">
                  {selectedImageIndex + 1} / {selectedWork.images.length}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
